/**
 * Auth Service — handles authentication via Next.js proxy routes
 * Tokens are stored in HttpOnly cookies, auto-sent with requests
 */
import { authConfig } from "@/lib/config/auth.config";
import { handle403Pivot } from "@/lib/auth/handle-403-pivot";
import { sanitizeUserFacingErrorMessage } from "@/lib/error/user-facing-error";
import {
  BACKEND_UNAVAILABLE_ID,
  isBackendUnavailableError,
  useBackendHealthStore,
} from "@/stores/useBackendHealthStore";
import type {
  LoginWithEmailPayload,
  RegisterPayload,
  SignupWithInvitePayload,
  ForgotPasswordPayload,
  ResetPasswordPayload,
  VerifyEmailPayload,
  UpdateProfilePayload,
  ChangePasswordPayload,
  AuthResponse,
  RefreshResponse,
  MessageResponse,
  UserResponse,
  BatchUsersResponse,
  UserInfo,
} from "@/types/auth.types";

interface ApiResponse {
  success: boolean;
  data: unknown;
}

interface ApiError {
  message: string;
  statusCode?: number;
  /** Stable slug emitted by BE error envelope (`error.id`). Drives FE pivots
   *  for system-tier 403s (account_suspended, account_pending_approval, …). */
  id?: string;
}

export class AuthApiError extends Error {
  statusCode?: number;
  id?: string;

  constructor(message: string, source?: Pick<ApiError, "statusCode" | "id">) {
    super(message);
    this.name = "AuthApiError";
    this.statusCode = source?.statusCode;
    this.id = source?.id;
  }
}

let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

function redirectToLogin() {
  if (typeof window !== "undefined") {
    localStorage.removeItem("user");
    window.location.href = "/signin";
  }
}

/**
 * Try to refresh tokens. Deduplicates concurrent refresh attempts.
 */
async function tryRefresh(): Promise<boolean> {
  if (isRefreshing && refreshPromise) return refreshPromise;

  isRefreshing = true;
  refreshPromise = (async () => {
    try {
      const res = await fetch(authConfig.endpoints.refresh, {
        method: "POST",
        credentials: "include",
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

async function apiCall<T>(
  endpoint: string,
  options: RequestInit & { skipAuthRedirect?: boolean } = {},
): Promise<T> {
  const { skipAuthRedirect, ...fetchOptions } = options;

  const callEndpoint = async () => {
    try {
      return await fetch(endpoint, {
        ...fetchOptions,
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...fetchOptions.headers,
        },
      });
    } catch {
      const error: ApiError = {
        message: "Cannot connect to backend",
        statusCode: 0,
        id: BACKEND_UNAVAILABLE_ID,
      };
      useBackendHealthStore.getState().reportBackendFailure(error.message);
      throw error;
    }
  };

  let response = await callEndpoint();

  // On 401, ALWAYS try refresh once and retry — independent of redirect intent.
  // `skipAuthRedirect` only suppresses the /signin redirect for callers that
  // tolerate unauthenticated state (login form, hydrate); it must NOT skip the
  // silent refresh, otherwise hydrate after a 15-min access expiry logs the
  // user out even though the 30-day refresh token is still valid.
  if (response.status === 401) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      response = await callEndpoint();
    }
  }

  // Null-safe JSON parse so non-JSON responses (e.g. Cloudflare Workers
  // `502 Bad Gateway` HTML page when the BE is down) do not throw an
  // unhandled SyntaxError that surfaces to the user as gibberish like
  // `Unexpected token <`. Same idiom as `hubCall` in lib/api/hub.ts.
  const data = await response.json().catch(() => null);

  if (!response.ok) {
    if (response.status === 401 && !skipAuthRedirect) {
      redirectToLogin();
    }

    // Prefer the friendly fallback for 5xx so the operator does not see
    // proxy-internal strings like "fetch failed" / "ECONNREFUSED" — those
    // leak Node-fetch jargon into the login form. Below 500 we still trust
    // the BE-supplied message because 4xx errors usually carry actionable
    // detail (e.g. "Invalid credentials").
    // BE returns 4xx in two wire shapes (see CLAUDE.md "Error handling pattern"):
    //   - Wrapped `{ error: { id, code, status, message } }` from the Recovery
    //     middleware path (typed sentinel panic in controllers).
    //   - Flat `{ id, code, status, message }` from `c.AbortWithStatusJSON(...)`
    //     called directly (auth middleware uses this for 401/403 gate rejections,
    //     incl. account_pending_approval / account_suspended).
    // Read both so `err.id` is populated regardless of which path produced the
    // error — otherwise system-tier 403 slugs fall through to a /signin redirect.
    const beMessage = data?.error?.message || data?.message;
    const beSlug = data?.error?.id || data?.id;
    const friendly = defaultMessageForStatus(response.status);
    const error: ApiError = {
      message: response.status >= 500 ? friendly : beMessage || friendly,
      statusCode: response.status,
      id: beSlug,
    };
    if (isBackendUnavailableError(error)) {
      error.id = BACKEND_UNAVAILABLE_ID;
      useBackendHealthStore.getState().reportBackendFailure(error.message);
    }
    throw error;
  }

  return data;
}

// Status-aware fallback message used when the BE / proxy returns a non-JSON
// body (typical for 502 / 503 / 504 from Cloudflare Workers when the
// upstream is down). Keeping the copy English-only here matches the rest
// of authService — the form layer can decide to localize via
// i18n + statusCode if a richer message is required.
function defaultMessageForStatus(status: number): string {
  if (status >= 500) return "Server unreachable";
  if (status === 0) return "Network error";
  return "Request failed";
}

// Coerces whatever `apiCall` threw into a user-facing string. Keep the
// technical status on AuthApiError.statusCode for pivots/debugging; do not
// render HTTP codes in auth forms.
function humanizeAuthError(error: unknown, fallback: string): string {
  const apiError = error as ApiError | null;
  const base = apiError?.message?.trim() || fallback;
  return sanitizeUserFacingErrorMessage(base, fallback);
}

function toAuthApiError(error: unknown, fallback: string): AuthApiError {
  const apiError = error as ApiError | null;
  return new AuthApiError(humanizeAuthError(error, fallback), {
    statusCode: apiError?.statusCode,
    id: apiError?.id,
  });
}

export const authService = {
  async loginWithEmail(payload: LoginWithEmailPayload): Promise<AuthResponse> {
    try {
      return await apiCall<AuthResponse>(authConfig.endpoints.login, {
        method: "POST",
        body: JSON.stringify(payload),
        skipAuthRedirect: true,
      });
    } catch (error) {
      throw toAuthApiError(error, "Login failed");
    }
  },

  async register(payload: RegisterPayload): Promise<AuthResponse> {
    try {
      return await apiCall<AuthResponse>(authConfig.endpoints.register, {
        method: "POST",
        body: JSON.stringify(payload),
        skipAuthRedirect: true,
      });
    } catch (error) {
      throw toAuthApiError(error, "Registration failed");
    }
  },

  async signupWithInvite(payload: SignupWithInvitePayload): Promise<AuthResponse> {
    try {
      return await apiCall<AuthResponse>(authConfig.endpoints.signupWithInvite, {
        method: "POST",
        body: JSON.stringify({
          email: payload.email,
          password: payload.password,
          token: payload.token,
          first_name: payload.firstName,
          last_name: payload.lastName,
        }),
        skipAuthRedirect: true,
      });
    } catch (error) {
      throw toAuthApiError(error, "Invite signup failed");
    }
  },

  async refreshToken(): Promise<RefreshResponse> {
    try {
      return await apiCall<RefreshResponse>(authConfig.endpoints.refresh, {
        method: "POST",
      });
    } catch (error) {
      throw toAuthApiError(error, "Token refresh failed");
    }
  },

  async logout(): Promise<void> {
    try {
      await apiCall(authConfig.endpoints.logout, {
        method: "POST",
        skipAuthRedirect: true,
      });
    } catch (error) {
      console.warn("Logout API call failed:", error);
    }
  },

  async getCurrentUser(): Promise<UserResponse> {
    try {
      return await apiCall<UserResponse>(authConfig.endpoints.me, {
        method: "GET",
      });
    } catch (error) {
      throw toAuthApiError(error, "Failed to fetch user");
    }
  },

  async checkAuth(): Promise<UserResponse | null> {
    try {
      return await apiCall<UserResponse>(authConfig.endpoints.me, {
        method: "GET",
        skipAuthRedirect: true,
      });
    } catch (caught) {
      // /v1/me 403 with a system-tier slug (account_suspended,
      // account_pending_approval, …) must drive the FE pivot — otherwise the
      // bare null return falls through to a /signin redirect loop because the
      // hydrate path treats null as "not logged in" and retries forever.
      const err = caught as ApiError;
      if (err.statusCode === 403 && err.id) {
        handle403Pivot({ id: err.id, statusCode: err.statusCode });
      }
      return null;
    }
  },

  async forgotPassword(payload: ForgotPasswordPayload): Promise<MessageResponse> {
    try {
      return await apiCall<MessageResponse>(authConfig.endpoints.forgotPassword, {
        method: "POST",
        body: JSON.stringify(payload),
        skipAuthRedirect: true,
      });
    } catch (error) {
      const apiError = error as ApiError;
      throw new Error(apiError.message || "Failed to send reset email");
    }
  },

  async resetPassword(payload: ResetPasswordPayload): Promise<MessageResponse> {
    try {
      return await apiCall<MessageResponse>(authConfig.endpoints.resetPassword, {
        method: "POST",
        body: JSON.stringify(payload),
        skipAuthRedirect: true,
      });
    } catch (error) {
      const apiError = error as ApiError;
      throw new Error(apiError.message || "Failed to reset password");
    }
  },

  async verifyEmail(payload: VerifyEmailPayload): Promise<MessageResponse> {
    try {
      return await apiCall<MessageResponse>(authConfig.endpoints.verifyEmail, {
        method: "POST",
        body: JSON.stringify(payload),
        skipAuthRedirect: true,
      });
    } catch (error) {
      const apiError = error as ApiError;
      throw new Error(apiError.message || "Failed to verify email");
    }
  },

  async updateProfile(payload: UpdateProfilePayload): Promise<ApiResponse> {
    try {
      return await apiCall<ApiResponse>(authConfig.endpoints.me, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
    } catch (error) {
      const apiError = error as ApiError;
      throw new Error(apiError.message || "Failed to update profile");
    }
  },

  async changePassword(payload: ChangePasswordPayload): Promise<ApiResponse> {
    try {
      return await apiCall<ApiResponse>(authConfig.endpoints.changePassword, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
    } catch (error) {
      const apiError = error as ApiError;
      throw new Error(apiError.message || "Failed to change password");
    }
  },

  /**
   * Search users by exact email via Builder Auth S2S endpoint.
   * Returns matching users (usually 0 or 1).
   */
  async searchUserByEmail(email: string): Promise<UserInfo[]> {
    if (!email.trim()) return [];

    try {
      const response = await apiCall<{ success: boolean; data: UserInfo[] }>(
        `${authConfig.endpoints.usersSearch}?email=${encodeURIComponent(email.trim())}`,
        { method: "GET" },
      );
      return response.data || [];
    } catch (error) {
      console.error("Failed to search user by email:", error);
      return [];
    }
  },

  /**
   * Batch fetch user info by IDs via Builder Auth S2S endpoint.
   * Returns a Map<userId, UserInfo> for easy lookup.
   */
  async batchGetUsers(userIds: string[]): Promise<Map<string, UserInfo>> {
    if (userIds.length === 0) return new Map();

    // Deduplicate
    const uniqueIds = [...new Set(userIds)];

    try {
      const response = await apiCall<BatchUsersResponse>(authConfig.endpoints.usersBatch, {
        method: "POST",
        body: JSON.stringify({ userIds: uniqueIds }),
      });

      const map = new Map<string, UserInfo>();
      if (response.data) {
        for (const user of response.data) {
          map.set(user.id, user);
        }
      }
      return map;
    } catch (error) {
      console.error("Failed to batch fetch users:", error);
      return new Map();
    }
  },
};
